import React, { useMemo } from 'react';
import { Field, useForm } from 'react-final-form';
import { useIntl } from 'react-intl';

import { List, ListItemProps, PillAction, Popper, StatusDot } from '@xsolla/ui-core';

import { SMSProviderFields } from '../../../api';
import { StatusPopper } from '../style';

type Props = {
  providerData: SMSProviderFields | Record<string, unknown>;
  dataTestId?: string;
};

export const SMSProviderStatus = ({ providerData, dataTestId }: Props): JSX.Element => {
  const intl = useIntl();
  const { name, is_enabled: isActive } = providerData;
  const { change } = useForm();

  const toggleStatus = () => {
    change(`fields.${name}.is_enabled`, !isActive);
  };

  const activeOptions: ListItemProps[] = [
    {
      children: intl.formatMessage({
        id: 'custom-providers.settings.sms.fields-group.list-item.deactivate'
      }),
      onClick: toggleStatus,
      icon: () => <StatusDot palette="warning" />,
      iconAlign: 'right'
    }
  ];

  const inactiveOptions: ListItemProps[] = [
    {
      children: intl.formatMessage({
        id: 'custom-providers.settings.sms.fields-group.list-item.activate'
      }),
      onClick: toggleStatus,
      icon: () => <StatusDot palette="action" />,
      iconAlign: 'right'
    }
  ];

  const ListItems = useMemo(() => {
    if (isActive) {
      return activeOptions;
    }

    return inactiveOptions;
  }, [isActive]);

  return (
    <StatusPopper active={!!isActive} data-test-id={dataTestId}>
      {/* Empty field for "pristine" change  */}
      {/* // https://github.com/final-form/final-form/issues/169 */}
      <Field name={`fields.${name}.is_enabled`}>{() => null}</Field>

      <Popper entry={<List items={ListItems} />} placement="bottom-end">
        {(referenceProps) => (
          <PillAction type="button" {...referenceProps} palette={isActive ? 'action' : 'warning'}>
            {intl.formatMessage({
              id: `custom-providers.settings.sms.fields-group.${isActive ? 'active' : 'inactive'}`
            })}
          </PillAction>
        )}
      </Popper>
    </StatusPopper>
  );
};
